import {
  Typography,
  RadioButtonGroup,
  RadioButton,
  ToggleButtonGroup,
  ToggleButton,
  DatePicker
} from '@sanlam-glacier/app-common/components';
import { StatementPeriodTypeOptions, PeriodPresetOptions } from 'modules/clients/types/client.enum';
import { Control, Controller } from 'react-hook-form';
import styles from './statement-period-selection.module.scss';
import { StatementFormValues } from 'modules/clients/types/client.types';
import { validateDate } from 'modules/clients/utils/statement-form-helper';

type SingleSelectStatementPeriodProps = {
  control: Control<StatementFormValues, StatementFormValues, StatementFormValues>;
  min30DaysStart: Date;
  errorState: boolean;
  setErrorState: React.Dispatch<React.SetStateAction<boolean>>;
  customPeriodRadioButtonDisabled: boolean;
};

export const SingleSelectStatementPeriod = ({
  control,
  min30DaysStart,
  errorState,
  setErrorState,
  customPeriodRadioButtonDisabled
}: SingleSelectStatementPeriodProps) => {
  return (
    <>
      <Typography
        variant={Typography.VARIANT.H5}
        weight={Typography.WEIGHT.MEDIUM}
        color={Typography.COLOR.DEFAULT}
      >
        Statement Period
      </Typography>
      <Typography
        variant={Typography.VARIANT.B3}
        weight={Typography.WEIGHT.NORMAL}
        color={Typography.COLOR.INHERIT}
        className={styles.subHeading}
      >
        Choose between predefined periods to the current date,{' '}
        <span className={styles.bold}>or</span> set a custom period (at least 30 days).
      </Typography>

      <div className={styles.radioButtonContainer}>
        <Controller
          name='statementPeriodType'
          control={control}
          render={({ field }) => (
            <RadioButtonGroup
              name='date-range'
              onChange={(val) => {
                field.onChange(val);
              }}
            >
              <RadioButton
                aria-label={StatementPeriodTypeOptions.toCurrentDate}
                value={StatementPeriodTypeOptions.toCurrentDate}
                radioGroup='date-range'
              >
                To current date
              </RadioButton>

              <Controller
                name='periodPreset'
                control={control}
                render={({ field: chipField }) => (
                  <ToggleButtonGroup
                    assistiveText='Select one option'
                    exclusive={true}
                    className={styles.toggleButtonGroup}
                    onChange={(val) => {
                      chipField.onChange(val);
                    }}
                    size={ToggleButtonGroup.SIZE.XSMALL}
                  >
                    <ToggleButton
                      disabled={field.value !== StatementPeriodTypeOptions.toCurrentDate}
                      aria-label={PeriodPresetOptions.thirtyDays}
                      value={PeriodPresetOptions.thirtyDays}
                    >
                      30 days
                    </ToggleButton>
                    <ToggleButton
                      disabled={field.value !== StatementPeriodTypeOptions.toCurrentDate}
                      aria-label={PeriodPresetOptions.ninetyDays}
                      value={PeriodPresetOptions.ninetyDays}
                    >
                      90 days
                    </ToggleButton>
                    <ToggleButton
                      disabled={field.value !== StatementPeriodTypeOptions.toCurrentDate}
                      aria-label={PeriodPresetOptions.oneYear}
                      value={PeriodPresetOptions.oneYear}
                    >
                      1 year
                    </ToggleButton>
                    <ToggleButton
                      disabled={field.value !== StatementPeriodTypeOptions.toCurrentDate}
                      aria-label={PeriodPresetOptions.sinceInception}
                      value={PeriodPresetOptions.sinceInception}
                    >
                      Since inception
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              />

              <RadioButton
                aria-label={StatementPeriodTypeOptions.customPeriod}
                value={StatementPeriodTypeOptions.customPeriod}
                radioGroup='date-range'
                disabled={customPeriodRadioButtonDisabled}
              >
                Custom period
              </RadioButton>

              <Controller
                name='dateRange'
                control={control}
                render={({ field: datePickerField }) => (
                  <div className={styles.datePickerContainer}>
                    <DatePicker
                      date={datePickerField.value[0] as Date}
                      disabled={field.value !== StatementPeriodTypeOptions.customPeriod}
                      assistiveText='Minimum 30 days'
                      controlled
                      dateFormat='dd/MM/yyyy'
                      label='Select date from'
                      maxDate={min30DaysStart}
                      minDate={new Date('2015-09-30T09:32:41.201Z')}
                      onChange={(date) => {
                        datePickerField.onChange([date as Date, datePickerField.value[1]]);
                      }}
                      orderByLatestYear
                      selectsStart={true}
                      shouldCloseOnSelect
                      error={errorState}
                    />
                    <DatePicker
                      date={datePickerField.value[1] as Date}
                      disabled={field.value !== StatementPeriodTypeOptions.customPeriod}
                      controlled
                      dateFormat='dd/MM/yyyy'
                      label='Select date to'
                      maxDate={new Date()}
                      minDate={new Date('2015-09-30T09:32:41.201Z')}
                      onChange={(date) => {
                        datePickerField.onChange([datePickerField.value[0], date as Date]);
                        if (
                          !(
                            datePickerField.value[0] !== null &&
                            validateDate(datePickerField.value[0], date as Date)
                          )
                        ) {
                          setErrorState(true);
                        }
                      }}
                      orderByLatestYear
                      selectsEnd
                      shouldCloseOnSelect
                      error={errorState}
                    />
                  </div>
                )}
              />
            </RadioButtonGroup>
          )}
        />
      </div>
    </>
  );
};
